import {
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type ReactNode,
    type Ref,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowDown, ArrowUp, Calendar, Check, CheckCircle2, Clock, Copy, ListMusic, Loader2, Mail, MapPin, Phone, Plus, Trash2, UserPlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OrientationMismatchDialog } from '@/components/OrientationMismatchDialog';
import { PhotoUploadField } from '@/components/PhotoUploadField';
import {
    ShowCardsPanel,
    type ShowCardsPanelHandle,
} from '@/components/CardEditor/ShowCardsPanel';
import { FEATURES } from '@/config/features';
import { usePublicAccountSettings } from '@/hooks/usePublicAccountSettings';
import { ApplyToSeriesDialog } from '@/components/ApplyToSeriesDialog';
import {
    SHOW_FIELD_LABELS,
    missingShowFields,
    type InviteFormHandle,
    type InviteFormStatus,
} from '@/components/inviteRequirements';
import {
    summarizeHappening,
    // Pure client helpers (no network) — kept:
    getArtistsFromHappening,
    generateInviteLink,
    type Happening,
    type HappeningUpdate,
    type HappeningArtist,
    type HappeningWithArtists,
    type Person,
    type Place,
    type Thing,
    type TracklistItem,
} from '@littlebooker/dal';
// The anonymous show flow now goes through the public API (token-validated,
// server bypasses RLS) instead of the in-browser DAL — RLS Phase 3.2.
import { apiGetPublic, apiPostPublic, apiPatchPublic } from '@/lib/api';

type GearRequestFormItem = {
    id: string;
    thing_type: string;
    quantity: number;
    notes: string;
};

type ShowFormState = {
    title: string;
    description: string;
    imageUrl: string;
    imageFocus: string;
    genres: string[];
    genreInput: string;
    gearRequests: GearRequestFormItem[];
    riderNotes: string;
    tracklist: TracklistItem[];
    dietaryRestrictions: string;
    arrivalTime: string;
};

type FieldErrors = Partial<Record<keyof ShowFormState, string>>;

type BannerVariant = 'info' | 'success' | 'warning' | 'error';

type StatusBannerProps = {
    icon?: ReactNode;
    title: string;
    body: string;
    variant: BannerVariant;
};

const bannerStyles: Record<BannerVariant, string> = {
    info: 'bg-blue-50 text-blue-900 border-blue-100',
    success: 'bg-emerald-50 text-emerald-900 border-emerald-100',
    warning: 'bg-amber-50 text-amber-900 border-amber-100',
    error: 'bg-red-50 text-red-900 border-red-100',
};

const mapHappeningToFormState = (happening: Happening | null, currentGearRequests?: GearRequestFormItem[]): ShowFormState => {
    const shouldUseCurrentRequests = currentGearRequests && currentGearRequests.length > 0;
    const mappedGearRequests = shouldUseCurrentRequests ? currentGearRequests : (happening?.metadata?.gear_requests?.map(gr => ({
        id: gr.thing_id,
        thing_type: gr.name,
        quantity: gr.quantity || 1,
        notes: gr.notes || '',
    })) ?? []);

    return {
        title: happening?.title ?? '',
        description: happening?.description ?? '',
        imageUrl: happening?.image_url ?? '',
        imageFocus: happening?.metadata?.image_focus ?? 'center',
        genres: happening?.metadata?.genres ?? [],
        genreInput: '',
        gearRequests: mappedGearRequests,
        riderNotes: happening?.metadata?.rider_notes ?? '',
        tracklist: (happening?.metadata?.tracklist as TracklistItem[]) ?? [],
        dietaryRestrictions: '',
        arrivalTime: '',
    };
};

function StatusBanner({ icon, title, body, variant }: StatusBannerProps) {
    return (
        <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${bannerStyles[variant]}`}>
            <div className="mt-0.5 text-lg">{icon}</div>
            <div>
                <p className="font-semibold">{title}</p>
                <p className="text-sm/relaxed opacity-90">{body}</p>
            </div>
        </div>
    );
}

function ShowDetails({ happening, place }: { happening: Happening | HappeningWithArtists; place?: Place | null }) {
    const metadata = happening.metadata || {};
    const artists = getArtistsFromHappening(happening as HappeningWithArtists);
    return (
        <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
                <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Show</p>
                    <p className="text-base font-semibold">{happening.title || 'Untitled Show'}</p>
                    <p className="text-sm text-muted-foreground">/{happening.slug}</p>
                    {metadata.show_type && (
                        <span className="mt-1 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                            {metadata.show_type === 'resident' ? 'Resident Show' : metadata.show_type === 'takeover' ? 'Takeover' : 'Guest Show'}
                        </span>
                    )}
                </div>
                <div className="space-y-2">
                    {happening.start_time && (
                        <div className="flex items-center gap-2 text-sm">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            {new Date(happening.start_time).toLocaleString(undefined, { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </div>
                    )}
                    {metadata.duration_minutes && (
                        <div className="flex items-center gap-2 text-sm">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            {metadata.duration_minutes} minutes
                        </div>
                    )}
                </div>
            </div>

            {place && (
                <div className="space-y-2 rounded-md border border-input bg-muted/30 p-3">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Venue</p>
                    <p className="text-sm font-semibold">{place.name}</p>
                    {place.address && (
                        <div className="flex items-start gap-2 text-sm text-muted-foreground">
                            <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                            <span>{place.address}</span>
                        </div>
                    )}
                    {place.emergency_contact_phone && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Phone className="h-4 w-4 shrink-0" />
                            <span>{place.emergency_contact_phone}</span>
                        </div>
                    )}
                </div>
            )}

            {artists.length > 0 && (
                <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Artists</p>
                    <div className="flex flex-wrap gap-2">
                        {artists.map((artist: HappeningArtist) => (
                            <div
                                key={artist.person_id}
                                className="flex items-center gap-2 rounded-full bg-secondary text-secondary-foreground px-3 py-1"
                            >
                                {artist.picture_url && (
                                    <img
                                        src={artist.picture_url}
                                        alt=""
                                        className="h-5 w-5 rounded-full object-cover"
                                    />
                                )}
                                <span className="text-sm">{artist.name || artist.slug || 'Unnamed'}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {metadata.gear_requests && metadata.gear_requests.length > 0 && (
                <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Available Gear</p>
                    <div className="flex flex-wrap gap-2">
                        {metadata.gear_requests.map((gear) => (
                            <span
                                key={gear.thing_id}
                                className="rounded-md border border-input bg-background px-2 py-1 text-xs"
                            >
                                {gear.name}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * `GET /public/show` stamps this onto the happening it returns. Off unless the series opted
 * into bulk edits, which is the default — an artist filling in one date should not be asked
 * whether to overwrite every later date in the series.
 */
type WithBulkEditFlag = { series_allows_bulk_edits?: boolean };

export interface ShowDetailsFormProps {
    happening: (Happening | HappeningWithArtists) & WithBulkEditFlag;
    stubToken: string;
    onSaveSuccess?: () => void;
    /**
     * Hide the sticky save bar. The combined invite drives both forms from a single
     * submit instead, so that neither half can be sent on its own.
     */
    hideSubmitButton?: boolean;
    /** Lets a host page validate and save this form (see InviteFormHandle). */
    controlRef?: Ref<InviteFormHandle>;
    /** Fires whenever the outstanding required fields or the dirty flag change. */
    onStatusChange?: (status: InviteFormStatus) => void;
}

export function ShowDetailsForm({
    happening,
    stubToken,
    onSaveSuccess,
    hideSubmitButton = false,
    controlRef,
    onStatusChange,
}: ShowDetailsFormProps) {
    const queryClient = useQueryClient();
    // Token-scoped account prefs via the public API (stub token) — the anon show
    // page can't read the account row via the DAL once RLS is on.
    const accountSettings = usePublicAccountSettings('show', stubToken);
    const preferredImageRatio = accountSettings.preferred_image_ratio;
    // The card templates are one account's brand, so the server says whether this
    // show's account gets them.
    const socialCardsEnabled = accountSettings.social_cards_enabled;

    const [formState, setFormState] = useState<ShowFormState>(() => mapHappeningToFormState(happening));
    const [formErrors, setFormErrors] = useState<FieldErrors>({});
    const [imageMismatched, setImageMismatched] = useState(false);
    const [imageIsSquare, setImageIsSquare] = useState(false);
    const [showMismatchDialog, setShowMismatchDialog] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [showSavedMessage, setShowSavedMessage] = useState(false);
    const [genreSuggestions, setGenreSuggestions] = useState<string[]>([]);
    const [showGenreDropdown, setShowGenreDropdown] = useState(false);
    const [showApplyToSeriesDialog, setShowApplyToSeriesDialog] = useState(false);
    const [pendingPayload, setPendingPayload] = useState<HappeningUpdate | null>(null);
    const [tracklistPasteText, setTracklistPasteText] = useState('');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const suppressDirtyRef = useRef(false);
    const isFirstRender = useRef(true);
    // Mirrors hasUnsavedChanges so the refetch effect below can read it without
    // taking it as a dependency (which would re-run the reset on every keystroke).
    const hasUnsavedChangesRef = useRef(false);
    hasUnsavedChangesRef.current = hasUnsavedChanges;

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (suppressDirtyRef.current) {
            suppressDirtyRef.current = false;
            return;
        }
        setHasUnsavedChanges(true);
    }, [formState, tracklistPasteText]);

    const parseTracklistText = (text: string): TracklistItem[] => {
        const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
        return lines.map(line => {
            const separatorIndex = line.indexOf(' - ');
            if (separatorIndex > 0) {
                return {
                    artist: line.substring(0, separatorIndex).trim(),
                    title: line.substring(separatorIndex + 3).trim(),
                    timestamp: null,
                };
            }
            return { artist: '', title: line, timestamp: null };
        });
    };

    // Scroll to hash target (e.g. #tracklist) after component mounts
    useEffect(() => {
        const hash = window.location.hash;
        if (hash) {
            // Small delay to ensure DOM is fully rendered
            const timer = setTimeout(() => {
                const el = document.querySelector(hash);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 300);
            return () => clearTimeout(timer);
        }
    }, []);

    // Takeover artist management
    const isTakeover = happening?.metadata?.show_type === 'takeover';
    const [newArtistName, setNewArtistName] = useState('');
    const [newArtistEmail, setNewArtistEmail] = useState('');
    const [copiedArtistId, setCopiedArtistId] = useState<string | null>(null);
    const [emailSentForIds, setEmailSentForIds] = useState<Set<string>>(new Set());
    const [sendingEmailForId, setSendingEmailForId] = useState<string | null>(null);
    const [isSendingAllEmails, setIsSendingAllEmails] = useState(false);

    // Fetch full Person records for all happening artists (gives us invite_token + email)
    const happeningArtists = getArtistsFromHappening(happening as HappeningWithArtists);
    const { data: takeoverArtistPersons = [] } = useQuery({
        queryKey: ['takeoverArtistPersons', happening.id, happeningArtists.map((a) => a.slug).join(',')],
        // The server returns the happening's artist participants as mapped Person[].
        queryFn: () => apiGetPublic<Person[]>('/show/artists', { token: stubToken }),
        enabled: isTakeover && happeningArtists.length > 0,
    });

    // Build display list: full Person data + generated invite link
    const takeoverArtists = useMemo(() =>
        takeoverArtistPersons.map((person) => ({
            ...person,
            inviteLink: generateInviteLink(person, window.location.origin),
        })),
        [takeoverArtistPersons]
    );

    const createArtistMutation = useMutation({
        mutationFn: ({ name, email }: { name: string; email: string }) =>
            // Server creates the person (scoped to this happening's account) + the
            // participant row in one call.
            apiPostPublic<Person>('/show/artists', { token: stubToken }, { name, email }),
        onSuccess: () => {
            setNewArtistName('');
            setNewArtistEmail('');
            queryClient.invalidateQueries({ queryKey: ['happening', happening.slug] });
            queryClient.invalidateQueries({ queryKey: ['takeoverArtistPersons', happening.id] });
        },
    });

    const handleCopyInviteLink = async (artistId: string, inviteLink: string) => {
        await navigator.clipboard.writeText(inviteLink);
        setCopiedArtistId(artistId);
        setTimeout(() => setCopiedArtistId(null), 2000);
    };

    const handleSendInviteEmail = async (artist: Person) => {
        setSendingEmailForId(artist.id);
        try {
            // Server derives the recipient from the participant and enqueues it.
            await apiPostPublic('/show/reminders', { token: stubToken }, { entity_id: artist.id });
            setEmailSentForIds((prev) => new Set(prev).add(artist.id));
        } finally {
            setSendingEmailForId(null);
        }
    };

    const handleEmailAllArtists = async () => {
        const unsent = takeoverArtists.filter((a) => !emailSentForIds.has(a.id));
        if (unsent.length === 0) return;
        setIsSendingAllEmails(true);
        try {
            for (const artist of unsent) {
                await handleSendInviteEmail(artist);
            }
        } finally {
            setIsSendingAllEmails(false);
        }
    };

    // Fetch available gear/things for dropdown
    const { data: availableThings = [] } = useQuery({
        queryKey: ['public-show-things', stubToken],
        queryFn: () => apiGetPublic<Thing[]>('/show/things', { token: stubToken }),
    });

    // Determine place_id from column or metadata for equipment lookup
    const placeId = useMemo(() => {
        const metaPlaceId = happening?.metadata && typeof happening.metadata === 'object'
            ? (happening.metadata as { place_id?: string }).place_id
            : undefined;
        return happening?.place_id || metaPlaceId || null;
    }, [happening]);

    // Fetch place details to get layout image
    const { data: placeDetails } = useQuery<Place | null>({
        queryKey: ['public-show-place', stubToken, placeId],
        // The server derives the place from the token'd happening.
        queryFn: () => apiGetPublic<Place | null>('/show/place', { token: stubToken }),
        enabled: !!placeId,
    });

    // Fetch place equipment summary
    const { data: placeEquipmentSummary, isLoading: isLoadingPlaceEquipment, error: placeEquipmentError } = useQuery({
        queryKey: ['public-show-place-equipment', stubToken, placeId],
        queryFn: () => apiGetPublic('/show/place-equipment', { token: stubToken }),
        enabled: !!placeId,
    });

    // Separate equipment into default and requestable, excluding already requested items
    const { defaultEquipment, requestableEquipment } = useMemo(() => {
        const requestedThingIds = new Set(formState.gearRequests.map((r) => r.id));

        if (!placeEquipmentSummary) {
            const allRequestable = availableThings.filter(
                (thing) => !requestedThingIds.has(thing.id)
            );
            return { defaultEquipment: [], requestableEquipment: allRequestable };
        }

        const defaultEq = (placeEquipmentSummary.default_equipment || []).filter(
            (eq) => eq && eq.id && !requestedThingIds.has(eq.id)
        );

        const requestableEq = (placeEquipmentSummary.on_request_equipment || []).filter(
            (eq) => eq && eq.id && !requestedThingIds.has(eq.id)
        );

        return { defaultEquipment: defaultEq, requestableEquipment: requestableEq };
    }, [placeEquipmentSummary, formState.gearRequests, availableThings]);

    const equipmentLayoutUrl = useMemo(() => {
        const placeMetaUrl = placeDetails && placeDetails.metadata && typeof placeDetails.metadata === 'object'
            ? (placeDetails.metadata as { equipment_layout_image_url?: string }).equipment_layout_image_url
            : '';
        if (placeMetaUrl) return placeMetaUrl;
        if (!happening?.metadata || typeof happening.metadata !== 'object') return '';
        const meta = happening.metadata as { equipment_layout_image_url?: string };
        return meta.equipment_layout_image_url || '';
    }, [placeDetails, happening]);

    // The card's headline name: the booked artist, falling back to the show title
    // (a takeover or a series episode may have no artist attached yet).
    const cardArtistName = happeningArtists[0]?.name || happening?.title || '';

    // The cards have no save button of their own — saving the form saves them, via
    // this handle (see the updateMutation's onSuccess).
    const cardsPanelRef = useRef<ShowCardsPanelHandle>(null);

    // Cards get their own endpoint rather than riding on PATCH /show: that path
    // marks the stub complete (and notifies staff) on its first write, and saving a
    // card is not the artist declaring the show details done. The server also sweeps
    // the objects the save replaces, so a re-save leaves no second copy.
    const saveSocialCards = async (cards: Record<string, string>) => {
        await apiPatchPublic<Happening>('/show/social-cards', { token: stubToken }, { cards });
        await queryClient.invalidateQueries({ queryKey: ['public-show', stubToken] });
    };

    const artistPhotoOptions = useMemo<HappeningArtist[]>(
        () =>
            ((happening?.metadata?.artists as HappeningArtist[] | undefined) ?? []).filter(
                (a) => Boolean(a.picture_url)
            ),
        [happening]
    );

    const summary = summarizeHappening(happening);
    const missingFields = summary?.missingFields ?? [];
    const isResidentShow = happening?.metadata?.show_type === 'resident';

    useEffect(() => {
        // A refetch of the happening (e.g. the combined invite page refreshing the show
        // after the profile step, or a social-card upload) must not wipe edits the artist
        // has typed but not saved. Derived reads like artistPhotoOptions come straight off
        // the prop, so the newly-uploaded artist photo still shows up either way.
        if (hasUnsavedChangesRef.current) {
            return;
        }
        suppressDirtyRef.current = true;
        setFormState((prevFormState) =>
            mapHappeningToFormState(happening, prevFormState.gearRequests)
        );
        setFormErrors({});
        setSubmitError(null);
        setHasUnsavedChanges(false);
    }, [happening]);

    useEffect(() => {
        if (!showSavedMessage) {
            return undefined;
        }
        const timeout = setTimeout(() => setShowSavedMessage(false), 4000);
        return () => clearTimeout(timeout);
    }, [showSavedMessage]);

    // Search genres as user types
    useEffect(() => {
        if (!formState.genreInput.trim()) {
            setGenreSuggestions([]);
            return;
        }
        const timer = setTimeout(async () => {
            const results = await apiGetPublic<string[]>('/show/genres', {
                token: stubToken,
                q: formState.genreInput,
            });
            setGenreSuggestions(results);
        }, 200);
        return () => clearTimeout(timer);
    }, [formState.genreInput, stubToken]);

    const updateMutation = useMutation({
        mutationFn: ({ payload, applyToFuture }: { payload: HappeningUpdate; applyToFuture?: boolean }) =>
            apiPatchPublic<Happening>('/show', { token: stubToken }, { ...payload, applyToFuture }),
        onSuccess: (updatedHappening) => {
            queryClient.setQueryData(['happening', happening.slug], updatedHappening);
            suppressDirtyRef.current = true;
            setFormState(mapHappeningToFormState(updatedHappening, formState.gearRequests));
            setFormErrors({});
            setSubmitError(null);
            setHasUnsavedChanges(false);
            setShowSavedMessage(true);
            onSaveSuccess?.();
            // Render + upload the announcement cards off the back of the save, once the
            // show (and its photo) is safely stored. Deliberately not awaited: the form
            // is already saved, and the panel shows its own progress and any failure.
            void cardsPanelRef.current?.saveIfNeeded();
        },
        onError: (mutationError: unknown) => {
            if (mutationError instanceof Error) {
                setSubmitError(mutationError.message);
            } else {
                setSubmitError('Could not save your show details yet. Please try again.');
            }
        },
    });

    const handleFieldChange =
        (field: keyof ShowFormState) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { value } = event.target;
            setFormState((prev) => ({ ...prev, [field]: value }));
        };

    const handleAddGenre = (genre: string) => {
        const trimmed = genre.trim();
        if (!trimmed || formState.genres.length >= 4) return;
        if (formState.genres.some((g) => g.toLowerCase() === trimmed.toLowerCase())) return;

        setFormState((prev) => ({
            ...prev,
            genres: [...prev.genres, trimmed],
            genreInput: '',
        }));
        setShowGenreDropdown(false);
    };

    const handleRemoveGenre = (genre: string) => {
        setFormState((prev) => ({
            ...prev,
            genres: prev.genres.filter((g) => g !== genre),
        }));
    };

    const handleGenreInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            handleAddGenre(formState.genreInput);
        }
    };

    // Show photo is mandatory — artists routinely fill in their artist photo and
    // leave the show without artwork. The artist-photo shortcut below makes reusing
    // an existing photo a single click, so there is no reason to skip it.
    const errorMessages: Record<'title' | 'description' | 'imageUrl', string> = {
        title: 'Title is required for resident shows',
        description: 'Description is required',
        imageUrl: 'A show photo is required — upload one or reuse an artist photo below',
    };

    const missingKeys = () => missingShowFields({ ...formState, isResidentShow });

    const validateForm = (): FieldErrors => {
        const errors: FieldErrors = {};
        for (const key of missingKeys()) {
            errors[key] = errorMessages[key];
        }
        return errors;
    };

    /** Shared by the form's own submit and by the imperative handle. */
    const submitForm = (): boolean => {
        if (!stubToken) return false;
        setSubmitError(null);

        const errors = validateForm();
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            return false;
        }

        // Auto-parse any unparsed tracklist text so it's not lost
        const extraTracks = tracklistPasteText.trim() ? parseTracklistText(tracklistPasteText) : [];
        const allTracks = [...formState.tracklist, ...extraTracks];
        if (extraTracks.length > 0) {
            setFormState(prev => ({ ...prev, tracklist: allTracks }));
            setTracklistPasteText('');
        }

        const payload: HappeningUpdate = {
            title: formState.title.trim() || undefined,
            description: formState.description.trim() || null,
            image_url: formState.imageUrl.trim() || null,
            metadata: {
                genres: formState.genres,
                gear_requests: formState.gearRequests.map((gr) => ({
                    thing_id: gr.id,
                    name: gr.thing_type,
                    quantity: gr.quantity,
                    notes: gr.notes,
                })),
                rider_notes: formState.riderNotes.trim() || null,
                tracklist: allTracks.length > 0 ? allTracks : undefined,
                image_focus: formState.imageFocus !== 'center' ? formState.imageFocus : undefined,
            },
        };

        // Series instances only get the apply-to-series prompt when the series opted into bulk
        // edits. The API enforces the same flag, so without it the choice would be ignored.
        if (happening?.series_id && happening.series_allows_bulk_edits === true) {
            setPendingPayload(payload);
            setShowApplyToSeriesDialog(true);
            return true;
        }

        updateMutation.mutate({ payload });
        return true;
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        submitForm();
    };

    useEffect(() => {
        onStatusChange?.({
            missing: missingShowFields({ ...formState, isResidentShow }).map((key) => SHOW_FIELD_LABELS[key]),
            dirty: hasUnsavedChanges,
        });
    }, [formState, isResidentShow, hasUnsavedChanges, onStatusChange]);

    useImperativeHandle(controlRef, () => ({
        validate: () => {
            const missing = missingKeys();
            setFormErrors(validateForm());
            return missing.map((key) => SHOW_FIELD_LABELS[key]);
        },
        save: () => submitForm(),
    }));

    return (
        <>
            <div className="space-y-6">
                <ShowDetails happening={happening} place={placeDetails} />

                <div className="rounded-lg border bg-muted/30 p-4">
                    <p className="text-sm font-semibold">Non-optional info left to submit:</p>
                    {missingFields.length > 0 ? (
                        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                            {missingFields.map((field) => (
                                <li key={field}>{field}</li>
                            ))}
                        </ul>
                    ) : (
                        <p className="mt-2 text-sm text-muted-foreground">
                            Looks like everything is filled out. Feel free to review and make updates below.
                        </p>
                    )}
                </div>

                <form id="show-details-form" onSubmit={handleSubmit} className="space-y-8">
                    {/* Show Details Section */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
                            Show Details
                        </h3>

                        {/* Show Title */}
                        <div className="space-y-1">
                            <label className="text-sm font-medium">
                                Show Title {isResidentShow && <span className="text-destructive">*</span>}
                            </label>
                        <input
                            value={formState.title}
                            onChange={handleFieldChange('title')}
                            className="w-full rounded-md border border-input bg-background px-3 py-2"
                            placeholder="Give your show a catchy name"
                        />
                        {!isResidentShow && (
                            <p className="text-xs text-muted-foreground">Optional for guest shows</p>
                        )}
                        {formErrors.title && <p className="text-xs text-destructive">{formErrors.title}</p>}
                    </div>

                    {/* Description */}
                    <div className="space-y-1">
                        <label className="text-sm font-medium">
                            Description <span className="text-destructive">*</span>
                        </label>
                        <textarea
                            value={formState.description}
                            onChange={handleFieldChange('description')}
                            rows={4}
                            className="w-full rounded-md border border-input bg-background px-3 py-2"
                            placeholder="Tell listeners what to expect from your show - the vibe, genres, any special guests..."
                        />
                        <p className="text-xs text-muted-foreground">
                            This will be shown on the show page and shared on socials.
                        </p>
                        {formErrors.description && (
                            <p className="text-xs text-destructive">{formErrors.description}</p>
                        )}
                    </div>

                    {/* Show Photo */}
                    <PhotoUploadField
                        label="Show Photo"
                        required
                        helper={`Upload artwork for your show. ${preferredImageRatio === 'portrait' ? 'Portrait (3:4) works best.' : 'Landscape (3:2) works best.'}`}
                        value={formState.imageUrl}
                        hideUrlInput
                        onChange={(url) => setFormState((prev) => ({ ...prev, imageUrl: url }))}
                        category="happening_photo"
                        identifier={happening.slug}
                        uploadAuth={{ token: stubToken, entityId: happening.id }}
                        imageFocus={formState.imageFocus}
                        preferredRatio={preferredImageRatio}
                        onOrientationMismatch={({ mismatched, isSquare }) => {
                            setImageMismatched(mismatched);
                            setImageIsSquare(isSquare);
                            if (mismatched) setShowMismatchDialog(true);
                        }}
                    />
                    {formErrors.imageUrl && (
                        <p className="text-xs text-destructive">{formErrors.imageUrl}</p>
                    )}

                    {/* Reuse an artist photo. Kept visible even once a show photo is set so
                        swapping back to the artist photo stays one click. */}
                    {artistPhotoOptions.length > 0 && (
                        <div className="rounded-md border border-dashed border-input p-3">
                            <p className="text-sm font-medium">
                                {formState.imageUrl ? 'Or use an artist photo instead' : 'No photo yet? Use an artist photo'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Happy to reuse the same picture? One click and you're done.
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {artistPhotoOptions.map((artist: HappeningArtist) => {
                                    const isSelected = formState.imageUrl === artist.picture_url;
                                    return (
                                        <button
                                            key={artist.person_id}
                                            type="button"
                                            onClick={() => {
                                                setFormState((prev) => ({
                                                    ...prev,
                                                    imageUrl: artist.picture_url!,
                                                }));
                                                setFormErrors((prev) => ({ ...prev, imageUrl: undefined }));
                                            }}
                                            className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm hover:border-primary ${
                                                isSelected ? 'border-primary bg-primary/5' : ''
                                            }`}
                                        >
                                            <img
                                                src={artist.picture_url!}
                                                alt=""
                                                className="h-6 w-6 rounded-full object-cover"
                                            />
                                            {isSelected
                                                ? 'Using this artist photo'
                                                : `Use ${artist.name || artist.slug || 'Artist'}'s photo`}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Announcement cards, built from the show photo above. Gated to the
                        accounts whose brand these templates are. */}
                    {socialCardsEnabled && FEATURES.socialCardInArtistFlow && (
                        <div className="space-y-3 border-t pt-6">
                            <div>
                                <label className="text-sm font-medium">Announcement cards</label>
                                <p className="text-xs text-muted-foreground">
                                    Your show photo, framed for Instagram and SoundCloud. Position each
                                    one; they are saved with the rest of the form.
                                </p>
                            </div>
                            <ShowCardsPanel
                                ref={cardsPanelRef}
                                photoSrc={formState.imageUrl || null}
                                artistName={cardArtistName}
                                showName={formState.title}
                                variant={happening?.metadata?.show_type === 'resident' ? 'resident' : 'guest'}
                                savedCards={happening?.metadata?.social_cards ?? {}}
                                uploadAuth={{ token: stubToken, entityId: happening.id }}
                                onSave={saveSocialCards}
                            />
                        </div>
                    )}

                    {/* Genres */}
                    <div className="space-y-3">
                        <div>
                            <label className="text-sm font-medium">Genres</label>
                            <p className="text-xs text-muted-foreground">
                                Max 4 genres. Type and press Enter or comma to add.
                            </p>
                        </div>
                        <div className="relative">
                            <input
                                value={formState.genreInput}
                                onChange={(e) => {
                                    setFormState((prev) => ({ ...prev, genreInput: e.target.value }));
                                    setShowGenreDropdown(true);
                                }}
                                onKeyDown={handleGenreInputKeyDown}
                                onFocus={() => setShowGenreDropdown(true)}
                                onBlur={() => setTimeout(() => setShowGenreDropdown(false), 200)}
                                disabled={formState.genres.length >= 4}
                                className="w-full rounded-md border border-input bg-background px-3 py-2"
                                placeholder={
                                    formState.genres.length >= 4 ? 'Max 4 genres reached' : 'Type a genre...'
                                }
                            />
                            {showGenreDropdown && genreSuggestions.length > 0 && (
                                <div className="absolute z-10 mt-1 max-h-32 w-full overflow-y-auto rounded-md border bg-background shadow-lg">
                                    {genreSuggestions.map((genre) => (
                                        <button
                                            key={genre}
                                            type="button"
                                            onClick={() => handleAddGenre(genre)}
                                            className="w-full px-3 py-2 text-left hover:bg-muted"
                                        >
                                            {genre}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {formState.genres.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {formState.genres.map((genre) => (
                                    <div
                                        key={genre}
                                        className="flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary"
                                    >
                                        {genre}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveGenre(genre)}
                                            className="hover:text-primary/70"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    </div>

                    {/* Takeover Artists Section */}
                    {isTakeover && (
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3 flex items-center gap-2">
                                <UserPlus className="h-5 w-5" />
                                Artists
                            </h3>
                            <p className="text-sm text-muted-foreground pl-4">
                                Add the artists playing during the takeover. Once added, you can send them an invite email
                                or copy their personal link and share it with them yourself (e.g. via WhatsApp or DM).
                                Each link takes the artist to a page where they can complete their profile.
                            </p>

                            <div className="space-y-3 pl-4">
                                {/*
                                  * Artist list with invite actions.
                                  *
                                  * These stay PROFILE-ONLY on purpose — do not "upgrade" them to the
                                  * combined invite the way the admin pages were. This form is served to
                                  * whoever holds the show's stub token, and a combined link bundles that
                                  * same token. Handing one to each takeover artist would give every one
                                  * of them edit rights over the whole show, which is a privilege
                                  * escalation rather than a convenience. Profile links are per-person and
                                  * carry no show access, so they are the right thing here.
                                  */}
                                {takeoverArtists.length > 0 && (
                                    <div className="space-y-2">
                                        <div className="flex flex-wrap gap-2">
                                            {takeoverArtists.map((artist) => (
                                                <div
                                                    key={artist.id}
                                                    className="flex items-center gap-2 rounded-full bg-secondary text-secondary-foreground px-3 py-1"
                                                >
                                                    {artist.picture_url && (
                                                        <img src={artist.picture_url} alt="" className="h-5 w-5 rounded-full object-cover" />
                                                    )}
                                                    <span className="text-sm">{artist.name || artist.slug || artist.email}</span>
                                                    {artist.inviteLink && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCopyInviteLink(artist.id, artist.inviteLink!)}
                                                                className="text-muted-foreground hover:text-foreground"
                                                                title="Copy profile link"
                                                            >
                                                                {copiedArtistId === artist.id ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleSendInviteEmail(artist)}
                                                                disabled={sendingEmailForId === artist.id || emailSentForIds.has(artist.id)}
                                                                className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                                                                title="Send invite email"
                                                            >
                                                                {sendingEmailForId === artist.id ? (
                                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                                ) : emailSentForIds.has(artist.id) ? (
                                                                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                                                                ) : (
                                                                    <Mail className="h-3.5 w-3.5" />
                                                                )}
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        {/* Email all button */}
                                        {takeoverArtists.some((a) => a.inviteLink && !emailSentForIds.has(a.id)) && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                disabled={isSendingAllEmails}
                                                onClick={handleEmailAllArtists}
                                            >
                                                {isSendingAllEmails ? (
                                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                                ) : (
                                                    <Mail className="h-4 w-4 mr-1" />
                                                )}
                                                Email all artists
                                            </Button>
                                        )}

                                        {/* Fallback instructions */}
                                        <p className="text-xs text-muted-foreground italic">
                                            If an artist doesn't receive the email, use the <Copy className="inline h-3 w-3" /> button to copy their personal link and send it to them directly.
                                        </p>
                                    </div>
                                )}

                                {/* Add artist form */}
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                                    <div className="flex-1 space-y-1">
                                        <label className="text-xs font-medium">Name</label>
                                        <input
                                            value={newArtistName}
                                            onChange={(e) => setNewArtistName(e.target.value)}
                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                            placeholder="Artist name"
                                        />
                                    </div>
                                    <div className="flex-1 space-y-1">
                                        <label className="text-xs font-medium">Email</label>
                                        <input
                                            type="email"
                                            value={newArtistEmail}
                                            onChange={(e) => setNewArtistEmail(e.target.value)}
                                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                            placeholder="artist@email.com"
                                        />
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        disabled={!newArtistEmail.trim() || createArtistMutation.isPending}
                                        onClick={() => createArtistMutation.mutate({ name: newArtistName.trim(), email: newArtistEmail.trim() })}
                                    >
                                        {createArtistMutation.isPending ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                        ) : (
                                            <UserPlus className="h-4 w-4 mr-1" />
                                        )}
                                        Add artist
                                    </Button>
                                </div>
                                {createArtistMutation.isError && (
                                    <p className="text-xs text-destructive">
                                        {createArtistMutation.error instanceof Error ? createArtistMutation.error.message : 'Failed to create artist'}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Technical Rider Section */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
                            Technical Rider
                        </h3>
                        <p className="text-sm text-muted-foreground pl-4">
                            Let us know what equipment you need for your set
                        </p>

                    {/* Gear Requests Content */}
                    <div className="space-y-4 rounded-lg border border-primary/20 bg-primary/5 p-4">

                        {/* Equipment layout / stage plot */}
                        {equipmentLayoutUrl && (
                            <div className="overflow-hidden rounded-md border bg-background">
                                <div className="flex items-center justify-between border-b px-3 py-2 text-xs text-muted-foreground">
                                    <span>Equipment layout / stage plot</span>
                                </div>
                                <img
                                    src={equipmentLayoutUrl}
                                    alt="Equipment layout"
                                    className="max-h-[420px] w-full object-contain bg-muted"
                                    loading="lazy"
                                />
                            </div>
                        )}

                        {/* Included Equipment (Always Available) */}
                        {defaultEquipment.length > 0 && (
                            <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                                <p className="text-sm font-medium text-emerald-900">✓ Always Included</p>
                                <p className="text-xs text-emerald-800">
                                    This equipment is always available at the venue for your set.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {defaultEquipment.map((equipment) => (
                                        <span
                                            key={equipment.id}
                                            className="inline-flex items-center rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-xs font-medium text-emerald-900"
                                        >
                                            {equipment.name}
                                            <span className="rounded bg-emerald-100 px-1 text-[11px] font-semibold text-emerald-800">
                                                ×{equipment.place_equipment?.default_quantity ?? 1}
                                            </span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Current gear requests */}
                        {formState.gearRequests.length > 0 && (
                            <div className="space-y-2">
                                <p className="text-xs font-medium text-muted-foreground">Requested Equipment</p>
                                {formState.gearRequests.map((request, index) => (
                                    <div
                                        key={request.id}
                                        className="flex items-center gap-2 rounded-md border bg-background p-2"
                                    >
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">{request.thing_type}</p>
                                            {request.notes && (
                                                <p className="text-xs text-muted-foreground">{request.notes}</p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-muted-foreground">×{request.quantity}</span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setFormState((prev) => ({
                                                        ...prev,
                                                        gearRequests: prev.gearRequests.filter((_, i) => i !== index),
                                                    }));
                                                }}
                                                className="text-muted-foreground hover:text-destructive"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Add gear request */}
                        {requestableEquipment.length > 0 && (
                            <div className="space-y-2">
                                <p className="text-xs font-medium text-muted-foreground">Request Additional Equipment</p>
                                <div className="flex flex-wrap gap-2">
                                    {requestableEquipment.map((equipment) => (
                                        <button
                                            key={equipment.id}
                                            type="button"
                                            onClick={() => {
                                                setFormState((prev) => ({
                                                    ...prev,
                                                    gearRequests: [
                                                        ...prev.gearRequests,
                                                        {
                                                            id: equipment.id,
                                                            thing_type: equipment.name,
                                                            quantity: 1,
                                                            notes: '',
                                                        },
                                                    ],
                                                }));
                                            }}
                                            className="flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs hover:border-primary"
                                        >
                                            <Plus className="h-3 w-3" />
                                            {equipment.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* No requestable equipment message */}
                        {defaultEquipment.length > 0 && requestableEquipment.length === 0 && formState.gearRequests.length === 0 && (
                            <p className="text-xs text-muted-foreground italic">
                                All available equipment is already included. If you need something special, note it in the technical notes below.
                            </p>
                        )}

                        {/* No equipment at all message */}
                        {defaultEquipment.length === 0 && requestableEquipment.length === 0 && (
                            <div className="space-y-2">
                                {isLoadingPlaceEquipment && (
                                    <p className="text-xs text-muted-foreground italic">
                                        Loading equipment...
                                    </p>
                                )}
                                {!isLoadingPlaceEquipment && (
                                    <p className="text-xs text-muted-foreground italic">
                                        No equipment available to request. Contact your host if you need specific gear.
                                    </p>
                                )}
                                {placeEquipmentError && (
                                    <p className="text-xs text-amber-600">
                                        Error loading equipment. Refresh the page if you need to request specific gear.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Rider Notes */}
                        <div className="space-y-1">
                            <label className="text-sm font-medium">Additional Technical Notes</label>
                            <textarea
                                value={formState.riderNotes}
                                onChange={(e) => setFormState((prev) => ({ ...prev, riderNotes: e.target.value }))}
                                rows={3}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                placeholder="Any other technical requirements, special setup needs, or notes for the engineer..."
                            />
                        </div>
                        </div>
                    </div>

                    {/* Tracklist Section */}
                    <div id="tracklist" className="space-y-4">
                        <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3 flex items-center gap-2">
                            <ListMusic className="h-5 w-5" />
                            Tracklist
                            <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                        </h3>
                        <p className="text-sm text-muted-foreground pl-4">
                            Share the tracks you played during your show. No rush — you can always come back to this page after the show to fill this in.
                        </p>

                        <div className="space-y-3 pl-4">
                            {/* Bulk paste textarea */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Paste your tracklist</label>
                                <textarea
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                    rows={4}
                                    placeholder={"Aphex Twin - Windowlicker\nBoards of Canada - Roygbiv\nAutechre - Gantz Graf"}
                                    value={tracklistPasteText}
                                    onChange={(e) => setTracklistPasteText(e.target.value)}
                                />
                                <p className="text-xs text-muted-foreground">
                                    One track per line, format: Artist - Track Name. You can preview them below to add timestamps, or just hit save — we'll parse them automatically.
                                </p>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={!tracklistPasteText.trim()}
                                    onClick={() => {
                                        const parsed = parseTracklistText(tracklistPasteText);
                                        setFormState(prev => ({
                                            ...prev,
                                            tracklist: [...prev.tracklist, ...parsed],
                                        }));
                                        setTracklistPasteText('');
                                    }}
                                >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Optional: preview & add timestamps
                                </Button>
                            </div>

                            {/* Parsed tracks list */}
                            {formState.tracklist.length > 0 && (
                                <div className="space-y-2">
                                    <p className="text-xs font-medium text-muted-foreground">{formState.tracklist.length} track{formState.tracklist.length !== 1 ? 's' : ''}</p>
                                    <div className="space-y-1">
                                        {formState.tracklist.map((track, index) => (
                                            <div key={index} className="flex items-center gap-2 rounded-md border bg-background p-2">
                                                <div className="flex flex-col gap-0.5 shrink-0">
                                                    <button
                                                        type="button"
                                                        disabled={index === 0}
                                                        onClick={() => {
                                                            setFormState(prev => {
                                                                const t = [...prev.tracklist];
                                                                [t[index - 1], t[index]] = [t[index], t[index - 1]];
                                                                return { ...prev, tracklist: t };
                                                            });
                                                        }}
                                                        className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                                                    >
                                                        <ArrowUp className="h-3 w-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={index === formState.tracklist.length - 1}
                                                        onClick={() => {
                                                            setFormState(prev => {
                                                                const t = [...prev.tracklist];
                                                                [t[index], t[index + 1]] = [t[index + 1], t[index]];
                                                                return { ...prev, tracklist: t };
                                                            });
                                                        }}
                                                        className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                                                    >
                                                        <ArrowDown className="h-3 w-3" />
                                                    </button>
                                                </div>
                                                <span className="text-xs text-muted-foreground w-6 text-right shrink-0">{index + 1}.</span>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm truncate">
                                                        {track.artist ? (
                                                            <><span className="font-medium">{track.artist}</span> — {track.title}</>
                                                        ) : (
                                                            <span>{track.title}</span>
                                                        )}
                                                    </p>
                                                </div>
                                                <input
                                                    type="text"
                                                    className="w-20 rounded-md border border-input bg-background px-2 py-1 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring shrink-0"
                                                    placeholder="HH:MM:SS"
                                                    value={track.timestamp || ''}
                                                    onChange={(e) => {
                                                        setFormState(prev => ({
                                                            ...prev,
                                                            tracklist: prev.tracklist.map((t, i) =>
                                                                i === index ? { ...t, timestamp: e.target.value || null } : t
                                                            ),
                                                        }));
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setFormState(prev => ({
                                                            ...prev,
                                                            tracklist: prev.tracklist.filter((_, i) => i !== index),
                                                        }));
                                                    }}
                                                    className="text-muted-foreground hover:text-destructive shrink-0"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="text-destructive hover:text-destructive"
                                        onClick={() => setFormState(prev => ({ ...prev, tracklist: [] }))}
                                    >
                                        <Trash2 className="h-3 w-3 mr-1" />
                                        Clear all tracks
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Locked fields notice */}
                    <div className="rounded-md border border-dashed border-input bg-muted/20 p-4">
                        <p className="text-sm font-medium">Locked by admin</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                            The following are set by the programming team and cannot be changed here:
                        </p>
                        <ul className="mt-2 list-disc pl-5 text-xs text-muted-foreground">
                            <li>Show date and time</li>
                            <li>Duration</li>
                            <li>Show type (resident/guest)</li>
                            {!isTakeover && <li>Featured artists</li>}
                        </ul>
                        <p className="mt-2 text-xs text-muted-foreground">
                            Contact your admin if any of these need to change.
                        </p>
                    </div>

                    {submitError && (
                        <StatusBanner
                            variant="error"
                            title="Could not save yet"
                            body={submitError}
                            icon={<AlertTriangle className="h-5 w-5" />}
                        />
                    )}

                    {showSavedMessage && (
                        <StatusBanner
                            variant="success"
                            title="Show details saved"
                            body="Thanks! You can revisit this link any time to make updates."
                            icon={<CheckCircle2 className="h-5 w-5" />}
                        />
                    )}

                    {/* Spacer so sticky bar doesn't overlap last form content */}
                    <div className="h-16" />
                </form>

                {/* Sticky save bar */}
                {!hideSubmitButton && (
                    <div className={`sticky bottom-0 z-10 -mx-4 border-t px-4 py-3 transition-colors duration-300 ${hasUnsavedChanges && !showSavedMessage ? 'bg-destructive/10 border-destructive/30' : 'bg-background/95 backdrop-blur-sm border-border'}`}>
                        <div className="flex items-center gap-3">
                            <Button
                                type="submit"
                                form="show-details-form"
                                className={`w-full md:w-auto ${hasUnsavedChanges && !showSavedMessage ? 'animate-nudge bg-destructive hover:bg-destructive/90 text-destructive-foreground' : ''}`}
                                disabled={updateMutation.isPending}
                            >
                                {updateMutation.isPending ? 'Saving…' : 'Save show details'}
                            </Button>
                            {hasUnsavedChanges && !showSavedMessage && (
                                <p className="text-sm font-medium text-destructive">
                                    You have unsaved changes
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {formState.imageUrl && imageMismatched && (
                <OrientationMismatchDialog
                    open={showMismatchDialog}
                    onOpenChange={setShowMismatchDialog}
                    imageUrl={formState.imageUrl}
                    preferredRatio={preferredImageRatio}
                    imageFocus={formState.imageFocus}
                    onImageFocusChange={(val) => setFormState((prev) => ({ ...prev, imageFocus: val }))}
                    isSquare={imageIsSquare}
                />
            )}

            {/* Apply to Series Dialog */}
            <ApplyToSeriesDialog
                open={showApplyToSeriesDialog}
                onOpenChange={(open) => {
                    setShowApplyToSeriesDialog(open);
                    if (!open) setPendingPayload(null);
                }}
                onApplyToThis={() => {
                    if (pendingPayload) {
                        setShowApplyToSeriesDialog(false);
                        setPendingPayload(null);
                        updateMutation.mutate({ payload: pendingPayload });
                    }
                }}
                onApplyToFuture={() => {
                    if (pendingPayload) {
                        setShowApplyToSeriesDialog(false);
                        setPendingPayload(null);
                        updateMutation.mutate({ payload: pendingPayload, applyToFuture: true });
                    }
                }}
                isLoading={updateMutation.isPending}
            />
        </>
    );
}
